import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { FormDrawer } from '@/components/feedback/drawer';
import { useToast } from '@/components/feedback/toast';
import { SelectField, TextAreaField, UploadField } from '@/components/forms';
import { WARRANTY_REASONS } from '@/features/requests';
import { ApiError } from '@/lib/api/errors';
import type { UploadMessages } from '@/lib/upload';
import { UPLOAD_PRESETS } from '@/lib/upload';

import { useWarrantyUploader } from '../../hooks';
import { toDeviceOptions } from '../format';
import { useCreateOrderWarrantyClaim, useWarrantyEligibleItems } from '../hooks';
import { claimWarrantySchema, warrantyReasonSchema } from '../schemas';
import type { ClaimWarrantyValues } from '../types';

/** Five options, ordered by the wire enum, labelled from the shared map.
 *
 *  Built **inside the component**, not at module scope: `requests ↔ orders` is a module cycle (the
 *  requests barrel re-exports `WarrantyFilterDrawer`, which imports the orders barrel, which reaches
 *  this file via `OrderActionsMenu`), and `./warranty/format` is re-exported *after* that cycle entry
 *  point, so a module-scope read would find `WARRANTY_REASONS` still `undefined` and throw — taking
 *  `warranty-claims-drawer.test.tsx` and `warranty-preview-drawer.test.tsx` down with it (verified).
 *  Do NOT hoist this, and do NOT mirror the map locally — ADR-0023 requires it imported, not copied.
 */
function reasonOptions() {
  return warrantyReasonSchema.options.map((value) => ({ value, label: WARRANTY_REASONS[value] }));
}

/** AC 112444's wording as client copy, not an echo of the server's message — one line, like every
 *  other toast in the app, rather than the AC's `Success` title over it. */
const SUBMITTED_MESSAGE = 'Warranty claim has been successfully submitted.';

/** ADR-0008 keeps the server's own explanation when there is one; AC 112444's fixed string is the
 *  fallback for a true transport failure. */
const SUBMIT_ERROR = 'We could not submit your warranty claim. Please try again.';

/** AC 112444 verbatim, overriding the uploader's generic defaults. Inline rather than the dark toast
 *  Figma 382:309239 draws — the AC says inline three times (ADR-0023).
 *  `satisfies`, not a plain annotation: every `UploadMessages` key is optional, so a variable typed
 *  by annotation skips excess-property checking and a mistyped key would compile and silently never
 *  render. */
const UPLOAD_MESSAGES = {
  invalidType: 'Only JPG, JPEG or PNG files are supported',
  tooLarge: 'File size must not exceed 5MB',
  tooMany: 'You can upload a maximum of 5 images',
} satisfies UploadMessages;

export type ClaimWarrantyDrawerProps = {
  orderId: string;
  /** Required by the `warranty_claim` presign entity, alongside `orderId` (BE, story 112444). */
  customerId: string;
  open: boolean;
  onClose: () => void;
};

/**
 * Raises one Warranty Claim against one **Unit** of one Device on the order, on the Customer's
 * behalf (112444). Reached from the Actions menu directly when the order has no claims yet *and* at
 * least one Device is eligible, or stacked on the Warranty Claims listing via its Raise Warranty
 * Claim pill — closing returns to whichever is underneath (ADR-0022 pop-back, inherited).
 *
 * A deliberate sibling of `RequestReplacementDrawer` rather than a generalisation of it (ADR-0023).
 * Submit is gated on `formState.isValid` with `mode: 'onChange'`, so the button tracks validity
 * live across all four mandatory fields; only `onSuccess` resets and closes, so a failed submit
 * retains the Device, Reason, Description and every attached image for free.
 */
export function ClaimWarrantyDrawer({
  orderId,
  customerId,
  open,
  onClose,
}: ClaimWarrantyDrawerProps) {
  const form = useForm<ClaimWarrantyValues>({
    resolver: zodResolver(claimWarrantySchema),
    defaultValues: { device: '', reason: undefined, description: '', images: [] },
    mode: 'onChange',
  });
  const { data: items, isPending } = useWarrantyEligibleItems(orderId, open);
  const uploader = useWarrantyUploader({ customerId, orderId });
  const create = useCreateOrderWarrantyClaim(orderId);
  const toast = useToast();

  const submit = (values: ClaimWarrantyValues) =>
    create.mutate(values, {
      onSuccess: () => {
        toast.success({ title: SUBMITTED_MESSAGE });
        // Back to `defaultValues`. We call the parent's `onClose`, so `FormDrawer`'s own
        // reset-on-close never runs for this path.
        form.reset();
        onClose();
      },
      onError: (e) => toast.error({ title: e instanceof ApiError ? e.message : SUBMIT_ERROR }),
    });

  return (
    <FormDrawer
      form={form}
      open={open}
      onClose={onClose}
      title="Claim Warranty"
      submitText="Submit"
      onSubmit={submit}
      loading={create.isPending}
      primaryDisabled={!form.formState.isValid}
    >
      {/* Every exclusion the ACs list — expired warranty, a Unit with a Pending or Approved claim
          or replacement, the Bundle itself — is simply absent from the response. Do not add
          frontend eligibility filtering (ADR-0023). */}
      <SelectField
        name="device"
        control={form.control}
        label="Select Device"
        placeholder="Select"
        options={toDeviceOptions(items ?? [])}
        loading={isPending}
        required
      />
      <SelectField
        name="reason"
        control={form.control}
        label="Reason for Warranty Claim"
        placeholder="Select"
        options={reasonOptions()}
        required
      />
      <TextAreaField
        name="description"
        control={form.control}
        label="Description"
        placeholder="Enter description"
        rows={4}
        maxLength={2000}
        showCount
        required
      />
      <UploadField
        key={`warranty-images-${open}`}
        name="images"
        control={form.control}
        label="Images"
        required
        {...UPLOAD_PRESETS.componentImages}
        onUpload={uploader}
        collapseWhenFilled
        showCounter
        addMoreLabel="Upload more images (JPG, JPEG or PNG)"
        messages={UPLOAD_MESSAGES}
      />
    </FormDrawer>
  );
}
